import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

/**
 * 报名表批量填写：
 * - price：按供货价折扣（百分比） / 固定申报价
 * - stock：统一活动库存
 */
export default function MarketingEnrollBatchFillModal({
  open = false,
  kind = 'price', // price | stock
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation('workbench');
  const [mode, setMode] = useState('discount'); // discount | fixed
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setMode('discount');
    setValue('');
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, kind]);

  if (!open) return null;

  const isPrice = kind === 'price';
  const isDiscount = isPrice && mode === 'discount';
  const title = isPrice ? '批量填写活动申报价' : '批量填写活动库存';
  const placeholder = isPrice
    ? (isDiscount ? '如 97' : '如 199')
    : '如 100';

  const handleConfirm = () => {
    const raw = String(value || '').trim();
    if (!raw) return;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return;
    if (isDiscount && n > 100) return;
    if (isPrice) {
      onConfirm?.({
        kind: 'price',
        mode,
        value: n,
      });
    } else {
      onConfirm?.({
        kind: 'stock',
        value: Math.trunc(n),
      });
    }
    onClose?.();
  };

  return createPortal(
    <div className="tpo-batch-modal tpo-enroll-batch-fill-modal" role="presentation">
      <div className="tpo-batch-modal__backdrop" aria-hidden="true" />
      <div
        className="tpo-batch-modal__panel tpo-enroll-batch-fill-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="tpo-batch-modal__head">
          <h2 className="tpo-batch-modal__title">{title}</h2>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="tpo-batch-modal__body">
          <p className="tpo-batch-modal__hint">
            {selectedHintText(isPrice)}
          </p>
          {isPrice ? (
            <div className="tpo-enroll-batch-fill-modal__modes" role="radiogroup" aria-label={fixedUi("申报价填写方式")}>
              <label className="tpo-enroll-batch-fill-modal__mode">
                <input
                  type="radio"
                  name="enroll-price-batch-mode"
                  checked={mode === 'discount'}
                  onChange={() => setMode('discount')}
                />
                <span>{fixedUi("按供货价折扣")}</span>
              </label>
              <label className="tpo-enroll-batch-fill-modal__mode">
                <input
                  type="radio"
                  name="enroll-price-batch-mode"
                  checked={mode === 'fixed'}
                  onChange={() => setMode('fixed')}
                />
                <span>{fixedUi("按统一报价")}</span>
              </label>
            </div>
          ) : null}
          {isDiscount ? (
            <div className="tpo-enroll-batch-fill-modal__percent">
              <input
                ref={inputRef}
                className="tpo-input tpo-enroll-batch-fill-modal__percent-input"
                type="number"
                min={0.01}
                max={100}
                step={0.01}
                value={value}
                placeholder={placeholder}
                aria-label={t('marketing.enDiscountPct')}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirm();
                  }
                }}
              />
              <span className="tpo-enroll-batch-fill-modal__percent-unit" aria-hidden="true">%</span>
            </div>
          ) : (
            <input
              ref={inputRef}
              className="tpo-input"
              type="number"
              min={1}
              step={1}
              value={value}
              placeholder={placeholder}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleConfirm();
                }
              }}
            />
          )}
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onClose}>
            {t('shop.cancel')}
          </button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary tpo-btn--sm"
            disabled={!String(value || '').trim()}
            onClick={handleConfirm}
          >
            {t('marketing.enOk')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function selectedHintText(isPrice) {
  return isPrice
    ? '将应用到当前勾选商品；未勾选时应用到当前列表全部商品。'
    : '将统一写入当前勾选商品的活动库存；未勾选时应用到当前列表全部商品。';
}

/**
 * 按百分比折扣计算申报价。
 * 例：97 → 97% → 供货价 × 0.97（即 97 折）
 */
export function calcDeclarePriceByDiscount(supplyPrice, percent) {
  const supply = Number(String(supplyPrice ?? '').replace(/[^0-9.-]/g, ''));
  const pct = Number(percent);
  if (!Number.isFinite(supply) || supply <= 0 || !Number.isFinite(pct) || pct <= 0) {
    return null;
  }
  const ratio = pct / 100;
  if (!Number.isFinite(ratio) || ratio <= 0) return null;
  const next = Math.round(supply * ratio);
  return next > 0 ? String(next) : null;
}
